import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ArrowUp } from 'lucide-react';
import { applications, products } from '../api';
import type { Product, ProduktVorgaben } from '../types';

/**
 * Was ein Produkt verkauft: eine Application oder mehrere (Bündel) — und mit
 * welchen Werten je Plan.
 *
 * Seit Stufe 2 (10.10.2026) hat das Produkt keine eigenen Vorlagen mehr. Jede
 * Application bringt ihre Vorlage und ihre Einstellungsfelder mit; das Produkt
 * stellt nur vor, was beim Anlegen einer Instanz in diese Felder geht. Was an
 * der Instanz ausdrücklich gesetzt wird, gewinnt.
 *
 * Eigene Komponente mit eigenen Hooks, nicht in der Produktseite: dort stehen
 * frühe `return`s, und Hooks dahinter ergeben eine weiße Seite.
 */
interface Zeile {
  applicationId: string;
  name: string;
  slug: string;
  felder: { key: string; label: string }[];
  vorlage: string | null;
  vorgaben: ProduktVorgaben;
}

const ALLE = '__alle__';

export default function ProduktApplicationsBereich({ product }: { product: Product }) {
  const qc = useQueryClient();
  const { data: plaene } = useQuery({ queryKey: ['products', product.id, 'plans'], queryFn: () => products.getPlans(product.id) });
  const { data: alleApps } = useQuery({ queryKey: ['applications'], queryFn: applications.list });

  const ausServer = useMemo<Zeile[]>(
    () =>
      (product.applications ?? []).map((l) => ({
        applicationId: l.applicationId,
        name: l.application.name,
        slug: l.application.slug,
        felder: (l.application.configFields ?? []).map((f) => ({ key: f.key, label: f.label })),
        vorlage: l.application.templates?.find((t) => t.status === 'ACTIVE')?.version ?? null,
        vorgaben: l.vorgaben ?? {},
      })),
    [product.applications],
  );
  const [zeilen, setZeilen] = useState<Zeile[]>(ausServer);
  useEffect(() => setZeilen(ausServer), [ausServer]);
  const [neu, setNeu] = useState('');
  const [neuerSchluessel, setNeuerSchluessel] = useState<Record<string, string>>({});
  /** Von Hand hinzugefügte Zeilen, solange sie noch keinen Wert haben. */
  const [zusatz, setZusatz] = useState<Record<string, string[]>>({});

  const geaendert = JSON.stringify(zeilen.map((z) => [z.applicationId, z.vorgaben])) !==
    JSON.stringify(ausServer.map((z) => [z.applicationId, z.vorgaben]));

  const speichern = useMutation({
    mutationFn: () => products.setApplications(product.id, zeilen.map((z) => ({ applicationId: z.applicationId, vorgaben: z.vorgaben }))),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products', product.id] });
      qc.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  function setzeWert(appId: string, plan: string, key: string, wert: string) {
    setZeilen((zs) =>
      zs.map((z) => {
        if (z.applicationId !== appId) return z;
        const v: ProduktVorgaben = { alle: { ...(z.vorgaben.alle ?? {}) }, plaene: { ...(z.vorgaben.plaene ?? {}) } };
        const ziel = plan === ALLE ? v.alle! : (v.plaene![plan] = { ...(v.plaene![plan] ?? {}) });
        if (wert === '') delete ziel[key];
        else ziel[key] = wert;
        return { ...z, vorgaben: v };
      }),
    );
  }

  const spalten = [{ id: ALLE, name: 'Alle Pläne' }, ...(plaene ?? []).map((p) => ({ id: p.id, name: p.name }))];
  const verfuegbar = (alleApps ?? []).filter((a) => !zeilen.some((z) => z.applicationId === a.id));

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-slate-500">
        {zeilen.length > 1
          ? 'Dieses Produkt ist ein Bündel: je Application entsteht beim Kunden eine eigene Instanz, mit eigener Vorlage und eigenem Release. Gehören zwei davon zusammen (etwa ojfm und bryve), verbindet der PC sie beim Anlegen selbst.'
          : 'Die Software hinter diesem Angebot. Vorlagen, Einstellungen und Releases werden an der Application gepflegt.'}{' '}
        Vorgaben hier gehen beim Anlegen einer Instanz in deren Einstellungen; ein dort gesetzter Wert gewinnt.
      </p>

      {zeilen.map((z, i) => {
        const schluessel = Array.from(new Set([
          ...z.felder.map((f) => f.key),
          ...(zusatz[z.applicationId] ?? []),
          ...Object.keys(z.vorgaben.alle ?? {}),
          ...Object.values(z.vorgaben.plaene ?? {}).flatMap((p) => Object.keys(p)),
        ]));
        return (
          <div key={z.applicationId} className="rounded-xl border border-slate-200 bg-surface">
            <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
              <Link to={`/applications/${z.applicationId}`} className="font-medium text-slate-900 hover:text-brand-700">{z.name}</Link>
              <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">{z.slug}</code>
              <span className="text-xs text-slate-500">{z.vorlage ? `Vorlage ${z.vorlage}` : 'keine aktive Vorlage'}</span>
              <div className="ml-auto flex items-center gap-2">
                {i > 0 && (
                  <button
                    title="Nach oben"
                    onClick={() => setZeilen((zs) => { const n = [...zs]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n; })}
                    className="text-slate-400 hover:text-slate-700"
                  >
                    <ArrowUp size={14} />
                  </button>
                )}
                {zeilen.length > 1 && (
                  <button
                    title="Aus dem Produkt nehmen"
                    onClick={() => setZeilen((zs) => zs.filter((x) => x.applicationId !== z.applicationId))}
                    className="text-slate-400 hover:text-red-600"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">Einstellung</th>
                    {spalten.map((s) => <th key={s.id} className="px-2 py-2 font-medium">{s.name}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {schluessel.length === 0 && (
                    <tr><td colSpan={spalten.length + 1} className="px-4 py-3 text-xs italic text-slate-400">Diese Application hat keine Einstellungsfelder.</td></tr>
                  )}
                  {schluessel.map((k) => {
                    const feld = z.felder.find((f) => f.key === k);
                    return (
                      <tr key={k}>
                        <td className="px-4 py-1.5">
                          <div className="text-xs text-slate-700">{feld?.label ?? k}</div>
                          <div className="font-mono text-[11px] text-slate-400">{k}</div>
                        </td>
                        {spalten.map((s) => (
                          <td key={s.id} className="px-2 py-1.5">
                            <input
                              value={(s.id === ALLE ? z.vorgaben.alle?.[k] : z.vorgaben.plaene?.[s.id]?.[k]) ?? ''}
                              onChange={(e) => setzeWert(z.applicationId, s.id, k, e.target.value)}
                              placeholder="—"
                              className="w-full min-w-[8rem] rounded border border-slate-200 bg-surface px-2 py-1 font-mono text-xs"
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-2 border-t border-slate-100 px-4 py-2">
              <input
                value={neuerSchluessel[z.applicationId] ?? ''}
                onChange={(e) => setNeuerSchluessel((n) => ({ ...n, [z.applicationId]: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') }))}
                placeholder="WEITERER_SCHLUESSEL"
                className="w-56 rounded border border-slate-200 bg-surface px-2 py-1 font-mono text-xs"
              />
              <button
                disabled={!neuerSchluessel[z.applicationId]}
                onClick={() => {
                  const k = neuerSchluessel[z.applicationId];
                  setZusatz((zs) => ({ ...zs, [z.applicationId]: [...(zs[z.applicationId] ?? []), k] }));
                  setNeuerSchluessel((n) => ({ ...n, [z.applicationId]: '' }));
                }}
                className="text-xs font-medium text-brand-600 hover:text-brand-800 disabled:opacity-40"
              >
                Zeile hinzufügen
              </button>
              <span className="text-[11px] text-slate-400">für Werte ohne eigenes Feld, z. B. Module</span>
            </div>
          </div>
        );
      })}

      <div className="flex flex-wrap items-center gap-3">
        <select value={neu} onChange={(e) => setNeu(e.target.value)} className="rounded-lg border border-slate-300 bg-surface px-2 py-1.5 text-sm">
          <option value="">Application hinzufügen …</option>
          {verfuegbar.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <button
          disabled={!neu}
          onClick={() => {
            const a = alleApps?.find((x) => x.id === neu);
            if (!a) return;
            setZeilen((zs) => [...zs, { applicationId: a.id, name: a.name, slug: a.slug, felder: [], vorlage: null, vorgaben: {} }]);
            setNeu('');
          }}
          className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-800 disabled:opacity-40"
        >
          <Plus size={14} /> Zum Produkt
        </button>
        <div className="ml-auto flex items-center gap-3">
          {speichern.isSuccess && !geaendert && <span className="text-xs text-emerald-700">Gespeichert.</span>}
          {speichern.error && (
            <span className="text-xs text-red-700">
              {(speichern.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (speichern.error as Error).message}
            </span>
          )}
          <button
            onClick={() => speichern.mutate()}
            disabled={!geaendert || speichern.isPending || zeilen.length === 0}
            className="rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-40"
          >
            {speichern.isPending ? 'Speichert …' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
